/**
 * The ministry's own photographs, shipped in `public/images`.
 *
 * These are the fallback for /gallery until gallery albums are created in
 * Strapi. They are deliberately NOT grouped into named events or dated: the
 * filenames carry no such information, and inventing attributions would put
 * false records on the site. Once albums exist in the CMS they take over
 * completely — see docs/CMS.md.
 *
 * Alt text is generic for the same reason. Real per-image alt text belongs in
 * the CMS media library, where whoever took the photographs can write it.
 */

export const LOCAL_PHOTOS = [
  'IMG_0996.jpg',
  'IMG_0866.jpg',
  'IMG_0997.jpg',
  'IMG_0970.jpg',
  'IMG_0911.jpg',
  'IMG_0951.jpg',
  'IMG_1093.jpg',
  'IMG_0777.jpg',
  'IMG_1077.jpg',
  'IMG_0928.jpg',
  'IMG_1035.jpg',
  'IMG_0641.jpg',
  'IMG_1061.jpg',
  'IMG_0628.jpg',
  'IMG_1025.jpg',
  'IMG_0681-2.jpg',
  'IMG_1019.jpg',
  'IMG_0632.jpg',
  'IMG_1079.jpg',
  'IMG_0636.jpg',
  'IMG_0633.jpg',
  'IMG_1066.jpg',
  'IMG_0827.jpg',
  'IMG_0966.jpg',
  'IMG_0833.jpg',
  'IMG_1017.jpg',
  'IMG_0946.jpg',
  'IMG_0149.jpg',
  'IMG_0944.jpg',
  'IMG_1081.jpg',
  'IMG_0767.jpg',
  'IMG_1110.jpg',
  'IMG_0973.jpg',
  'IMG_1014.jpg',
  'IMG_0770.jpg',
  'IMG_1084.jpg',
  'IMG_0912.jpg',
  'IMG_0274.jpg',
] as const;

export interface LocalPhoto {
  src: string;
  alt: string;
}

export function localPhotos(limit?: number): LocalPhoto[] {
  const files = limit ? LOCAL_PHOTOS.slice(0, limit) : LOCAL_PHOTOS;
  return files.map((file) => ({
    src: `/images/${file}`,
    alt: 'Photograph from a Throne Room worship event',
  }));
}

/**
 * Photographs chosen for brand-critical positions — hero, section openers.
 *
 * Curation is the lever here: wide congregational frames and images built from
 * light and gesture, rather than face-forward portraits, so the identity reads
 * as global while the ministry's real community stays fully visible throughout
 * the gallery, events and testimonies.
 */
export const FEATURE_PHOTOS = {
  hero: '/images/IMG_0996.jpg',
  welcome: '/images/IMG_1093.jpg',
  /* A wide view of the room rather than a face. The giving ask sits beside the
     impact figures, which are claims about many people — a single portrait
     carrying that reads as one person's testimony instead. */
  give: '/images/IMG_0681-2.jpg',
  watch: '/images/IMG_0970.jpg',
  testimony: '/images/IMG_0951.jpg',
  events: '/images/IMG_1077.jpg',
  about: '/images/IMG_0866.jpg',
  academy: '/images/IMG_1035.jpg',
  contact: '/images/IMG_0997.jpg',
} as const;

/**
 * Fallback runs for the two sections that crossfade between photographs.
 *
 * Used ONLY when the CMS supplies nothing for a section — see `slideSources`,
 * which never mixes these with CMS media. Ordered as a sequence rather than a
 * pile: a voice, then the room, then another voice, so the run reads as the
 * section's own argument being made twice over.
 *
 * Every photograph here is from the one concert shipped in `public/images`, so
 * the sets are kept disjoint — the hero and the welcome section sit on the same
 * screen, and the same frame appearing in both would look like a bug.
 */
export const FEATURE_SLIDES = {
  hero: [
    '/images/IMG_0996.jpg',
    '/images/IMG_0866.jpg',
    '/images/IMG_1035.jpg',
    '/images/IMG_1025.jpg',
  ],
  welcome: [
    '/images/IMG_1093.jpg',
    '/images/IMG_1077.jpg',
    '/images/IMG_0777.jpg',
    '/images/IMG_1019.jpg',
  ],
} as const;
